import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { collection, query, onSnapshot, where, orderBy } from 'firebase/firestore';
import ProService from '../../services/ProService';

const PERIODS = [
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' },
  { value: 'all', label: 'All Time' },
];

export default function AnalyticsPage() {
  const { currentUser } = useAuth();
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState(null);
  const [leadAnalytics, setLeadAnalytics] = useState(null); // eslint-disable-line no-unused-vars
  const [invoices, setInvoices] = useState([]);
  const [leads, setLeads] = useState([]);

  // Fetch invoices
  useEffect(() => {
    if (!currentUser?.uid) return;

    // Query invoices using creatorId (matches backend convention)
    const invoicesRef = query(
      collection(db, 'invoices'),
      where('creatorId', '==', currentUser.uid)
    );

    const unsubscribe = onSnapshot(invoicesRef, (snapshot) => {
      setInvoices(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    return () => unsubscribe();
  }, [currentUser?.uid]);

  // Fetch leads
  useEffect(() => {
    if (!currentUser?.uid) return;

    const leadsRef = collection(db, 'users', currentUser.uid, 'leads');
    const unsubscribe = onSnapshot(leadsRef, (snapshot) => {
      setLeads(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser?.uid]);

  // Fetch AI insights
  useEffect(() => {
    if (!currentUser?.uid) return;

    const fetchInsights = async () => {
      try {
        const data = await ProService.getBusinessInsights(currentUser.uid);
        setInsights(data);
      } catch (error) {
        console.error('Failed to fetch insights:', error);
      }
    };

    fetchInsights();
  }, [currentUser?.uid]);

  // Fetch lead analytics
  useEffect(() => {
    if (!currentUser?.uid) return;

    const fetchLeadAnalytics = async () => {
      try {
        const data = await ProService.getLeadAnalytics(currentUser.uid, period);
        setLeadAnalytics(data);
      } catch (error) {
        console.error('Failed to fetch lead analytics:', error);
      }
    };

    fetchLeadAnalytics();
  }, [currentUser?.uid, period]);

  // Calculate metrics
  const totalRevenue = invoices
    .filter(i => i.status === 'paid')
    .reduce((sum, i) => sum + (i.amount || 0), 0);

  const pendingRevenue = invoices
    .filter(i => i.status === 'pending' || i.status === 'sent')
    .reduce((sum, i) => sum + (i.amount || 0), 0);

  const paidInvoices = invoices.filter(i => i.status === 'paid').length;
  const pendingInvoices = invoices.filter(i => i.status === 'pending' || i.status === 'sent').length;

  const hotLeads = leads.filter(l => l.temperature === 'hot').length;
  const warmLeads = leads.filter(l => l.temperature === 'warm').length;
  const coldLeads = leads.filter(l => l.temperature === 'cold').length;

  const wonDeals = leads.filter(l => l.stage === 'won');
  const lostDeals = leads.filter(l => l.stage === 'lost'); // eslint-disable-line no-unused-vars
  const conversionRate = leads.length > 0
    ? ((wonDeals.length / leads.length) * 100).toFixed(1)
    : 0;

  const pipelineValue = leads
    .filter(l => !['won', 'lost'].includes(l.stage))
    .reduce((sum, l) => sum + (l.value || 0), 0);

  // Source breakdown
  const sourceBreakdown = leads.reduce((acc, lead) => {
    const source = lead.source || 'other';
    acc[source] = (acc[source] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="h-full flex flex-col bg-white dark:bg-[#0A0A0A] overflow-auto">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-[#1A1A1A] p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Analytics</h1>
            <p className="text-sm text-gray-500 dark:text-[#666666] mt-1">
              Track your business performance
            </p>
          </div>
          <div className="flex items-center gap-2 bg-gray-100 dark:bg-[#1A1A1A] rounded-lg p-1">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  period === p.value
                    ? 'bg-white dark:bg-[#2A2A2A] text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-[#666666]'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 p-6 space-y-6">
        {/* Revenue Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Revenue"
            value={`$${totalRevenue.toLocaleString()}`}
            subtitle={`${paidInvoices} paid invoices`}
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            iconBg="bg-green-500/20"
            iconColor="text-green-500"
          />
          <StatCard
            title="Pending Revenue"
            value={`$${pendingRevenue.toLocaleString()}`}
            subtitle={`${pendingInvoices} pending invoices`}
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            iconBg="bg-yellow-500/20"
            iconColor="text-yellow-500"
          />
          <StatCard
            title="Pipeline Value"
            value={`$${pipelineValue.toLocaleString()}`}
            subtitle={`${leads.filter(l => !['won', 'lost'].includes(l.stage)).length} active leads`}
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            }
            iconBg="bg-brand/20"
            iconColor="text-brand"
          />
          <StatCard
            title="Conversion Rate"
            value={`${conversionRate}%`}
            subtitle={`${wonDeals.length} won / ${leads.length} total`}
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            iconBg="bg-blue-500/20"
            iconColor="text-blue-500"
          />
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lead Temperature */}
          <div className="bg-white dark:bg-[#111111] rounded-xl border border-gray-200 dark:border-[#1A1A1A] p-6">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Lead Temperature</h3>
            <div className="space-y-4">
              <TemperatureBar label="Hot" count={hotLeads} total={leads.length} color="bg-red-500" />
              <TemperatureBar label="Warm" count={warmLeads} total={leads.length} color="bg-yellow-500" />
              <TemperatureBar label="Cold" count={coldLeads} total={leads.length} color="bg-blue-500" />
            </div>
            <div className="mt-6 pt-4 border-t border-gray-100 dark:border-[#1A1A1A]">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500 dark:text-[#666666]">Total Leads</span>
                <span className="font-semibold text-gray-900 dark:text-white">{leads.length}</span>
              </div>
            </div>
          </div>

          {/* Lead Sources */}
          <div className="bg-white dark:bg-[#111111] rounded-xl border border-gray-200 dark:border-[#1A1A1A] p-6">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Lead Sources</h3>
            <div className="space-y-3">
              {Object.entries(sourceBreakdown)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 5)
                .map(([source, count]) => (
                  <div key={source} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-[#999999] capitalize">
                      {source.replace('_', ' ')}
                    </span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-gray-100 dark:bg-[#1A1A1A] rounded-full overflow-hidden">
                        <div
                          className="h-full bg-brand rounded-full"
                          style={{ width: `${(count / leads.length) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white w-8 text-right">
                        {count}
                      </span>
                    </div>
                  </div>
                ))}
              {Object.keys(sourceBreakdown).length === 0 && (
                <p className="text-sm text-gray-500 dark:text-[#666666] text-center py-4">
                  No data yet
                </p>
              )}
            </div>
          </div>

          {/* AI Insights */}
          <div className="bg-white dark:bg-[#111111] rounded-xl border border-gray-200 dark:border-[#1A1A1A] p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 rounded-lg bg-gradient-to-r from-brand to-[#FF8C42] flex items-center justify-center">
                <svg className="w-3.5 h-3.5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">AI Insights</h3>
            </div>
            <div className="space-y-3">
              {insights?.insights?.slice(0, 3).map((insight, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`p-3 rounded-lg border-l-2 ${
                    insight.type === 'opportunity'
                      ? 'border-l-brand bg-brand/5'
                      : insight.type === 'attention'
                      ? 'border-l-red-500 bg-red-500/5'
                      : 'border-l-gray-400 bg-gray-50 dark:bg-[#0A0A0A]'
                  }`}
                >
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{insight.title}</p>
                  <p className="text-xs text-gray-500 dark:text-[#666666] mt-1">{insight.description}</p>
                </motion.div>
              ))}
              {(!insights?.insights || insights.insights.length === 0) && (
                <div className="text-center py-8">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-2">
                    <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-[#666666]">All caught up!</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Pipeline Stages */}
        <div className="bg-white dark:bg-[#111111] rounded-xl border border-gray-200 dark:border-[#1A1A1A] p-6">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-6">Pipeline Breakdown</h3>
          <div className="flex items-end gap-4">
            {[
              { stage: 'new', label: 'New', color: '#3B82F6' },
              { stage: 'contacted', label: 'Contacted', color: '#8B5CF6' },
              { stage: 'qualified', label: 'Qualified', color: '#F59E0B' },
              { stage: 'proposal', label: 'Proposal', color: '#EC4899' },
              { stage: 'negotiating', label: 'Negotiating', color: '#6366F1' },
              { stage: 'won', label: 'Won', color: '#10B981' },
              { stage: 'lost', label: 'Lost', color: '#EF4444' },
            ].map((item) => {
              const count = leads.filter(l => l.stage === item.stage).length;
              const maxCount = Math.max(...Object.values(
                leads.reduce((acc, l) => {
                  acc[l.stage] = (acc[l.stage] || 0) + 1;
                  return acc;
                }, {})
              ), 1);
              const height = (count / maxCount) * 120;

              return (
                <div key={item.stage} className="flex-1 flex flex-col items-center">
                  <span className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {count}
                  </span>
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height }}
                    className="w-full rounded-t-lg"
                    style={{ backgroundColor: item.color, minHeight: 4 }}
                  />
                  <span className="text-xs text-gray-500 dark:text-[#666666] mt-2 text-center">
                    {item.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Won Deals */}
          <div className="bg-white dark:bg-[#111111] rounded-xl border border-gray-200 dark:border-[#1A1A1A] p-6">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Recent Won Deals</h3>
            <div className="space-y-3">
              {wonDeals.slice(0, 5).map((lead) => (
                <div key={lead.id} className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-500/10 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{lead.name}</p>
                    <p className="text-xs text-gray-500 dark:text-[#666666]">{lead.projectType || 'Project'}</p>
                  </div>
                  <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                    ${(lead.value || 0).toLocaleString()}
                  </span>
                </div>
              ))}
              {wonDeals.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-[#666666] text-center py-4">No won deals yet</p>
              )}
            </div>
          </div>

          {/* Hot Leads */}
          <div className="bg-white dark:bg-[#111111] rounded-xl border border-gray-200 dark:border-[#1A1A1A] p-6">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Hot Leads</h3>
            <div className="space-y-3">
              {leads.filter(l => l.temperature === 'hot' && !['won', 'lost'].includes(l.stage)).slice(0, 5).map((lead) => (
                <div key={lead.id} className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-500/10 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{lead.name}</p>
                      <p className="text-xs text-gray-500 dark:text-[#666666]">Score: {lead.score}</p>
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-brand">
                    ${(lead.value || 0).toLocaleString()}
                  </span>
                </div>
              ))}
              {leads.filter(l => l.temperature === 'hot').length === 0 && (
                <p className="text-sm text-gray-500 dark:text-[#666666] text-center py-4">No hot leads</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="absolute inset-0 bg-white/50 dark:bg-black/50 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
}

function StatCard({ title, value, subtitle, icon, iconBg, iconColor }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-[#111111] rounded-xl border border-gray-200 dark:border-[#1A1A1A] p-6"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 dark:text-[#666666]">{title}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
          <p className="text-xs text-gray-400 dark:text-[#666666] mt-1">{subtitle}</p>
        </div>
        <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center`}>
          <span className={iconColor}>{icon}</span>
        </div>
      </div>
    </motion.div>
  );
}

function TemperatureBar({ label, count, total, color }) {
  const percentage = total > 0 ? (count / total) * 100 : 0;

  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-gray-600 dark:text-[#999999] w-12">{label}</span>
      <div className="flex-1 h-2.5 bg-gray-100 dark:bg-[#1A1A1A] rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          className={`h-full ${color} rounded-full`}
        />
      </div>
      <span className="text-sm font-medium text-gray-900 dark:text-white w-8 text-right">
        {count}
      </span>
    </div>
  );
}
